import { type Vec2 } from '../math';
import { type EntityId } from '../model/ids';
import { type Euler, type ProjectionKind, type SolidKind } from '../solid';

/**
 * Construction definitions — the concrete form of ADR-0003's Tier 1.
 *
 * An `EntitySource` says *how an entity is determined*, never what it currently looks
 * like. `{ op: 'midpoint', a: 'p1', b: 'p2' }` is a definition that evaluates to exactly
 * one point; dragging `p1` re-evaluates it exactly, in one pass, with no iteration and
 * no error.
 *
 * Three consequences follow from storing definitions rather than coordinates, and they
 * are the reason the whole architecture is shaped this way:
 *
 * - **The DAG is implicit.** `parentsOf` reads the edges straight out of the source, so
 *   there is no separate graph structure to keep in sync with the document.
 * - **The file format is small and stable.** A construction of 500 entities saves as 500
 *   short records; no evaluated coordinates are persisted, so a file describes the
 *   construction rather than a snapshot of its appearance.
 * - **Replay is free.** A topological order of the DAG *is* a valid construction
 *   sequence, which is what "replay construction" and "step-by-step" render.
 *
 * Adding an operation means adding a variant here and an evaluator in M5. Because every
 * dispatch site is a `switch` over this union and
 * `@typescript-eslint/switch-exhaustiveness-check` is an error, the compiler enumerates
 * every place that needs updating.
 */

/* --------------------------------------------------------------- measurement kinds */

export const MeasureKind = {
  Length: 'length',
  Distance: 'distance',
  Angle: 'angle',
  Area: 'area',
  Perimeter: 'perimeter',
  Radius: 'radius',
  Diameter: 'diameter',
  Coordinates: 'coordinates',
  Slope: 'slope',
  VectorComponents: 'vector-components',
} as const;
export type MeasureKind = (typeof MeasureKind)[keyof typeof MeasureKind];

/* ------------------------------------------------------------------------- points */

/** A point the user can drag. The only place raw coordinates live in the document. */
export interface FreePoint {
  readonly op: 'free-point';
  readonly position: Vec2;
}

/**
 * A point constrained to a host curve, stored as a parameter along it.
 *
 * This is how `On Line`, `On Circle` and `On Segment` are modelled — as Tier 1
 * constructions, not as solver constraints. The point is therefore *exactly* on its host
 * at all times, rather than approximately so after convergence. Dragging projects the
 * pointer onto the host and updates `t`.
 */
export interface LocusPoint {
  readonly op: 'locus-point';
  readonly host: EntityId;
  readonly t: number;
  /**
   * Which side of the host, when the host is a polygon rather than a single curve.
   *
   * ## Why a polygon needs this and a circle does not
   *
   * Because a polygon is not one curve, and pretending otherwise gives the wrong number. A
   * triangle drawn as a `polygon` has a boundary you could parameterise end to end, but then
   * `t` would be a fraction of the **perimeter** — and what a problem asks for is a fraction of
   * *one side*: "M trên AB sao cho AM = 2/3 AB". Two thirds along AB is not two thirds round
   * the triangle, and a control offering the second while the user reads the first is worse
   * than no control.
   *
   * Naming the side makes `t` mean the same thing it means on a segment, which is what lets a
   * point on a triangle's side behave exactly like a point on a drawn segment — same drag, same
   * ratio panel, same exactness.
   *
   * ## What it is not
   *
   * Not an index into anything the user can see. It counts the ring from the first vertex, so a
   * polygon that loses vertices can leave it pointing past the end; the point then evaluates to
   * nothing rather than to somewhere arbitrary. Reversible, because the number is kept — give
   * the polygon its sides back and the point returns, exactly as a vanished intersection does.
   *
   * Absent means the host is a curve in its own right, which is every locus point that existed
   * before this and most of them since.
   */
  readonly edge?: number;
}

export interface Midpoint {
  readonly op: 'midpoint';
  readonly a: EntityId;
  readonly b: EntityId;
}

/**
 * A point dividing `a`→`b` in a given ratio.
 *
 * ## Why this is not a `locus-point` with a `t`
 *
 * It nearly is, and the difference matters. A locus point is attached to a *host curve*, so
 * dividing `AB` would require the segment to have been drawn — but a problem saying "gọi M
 * trên AB sao cho AM = 2/3 AB" is often stated before anything is drawn, and the point it
 * names exists whether or not anyone joined A to B. Taking the two endpoints instead makes
 * this the exact sibling of {@link Midpoint}, which is what it is.
 *
 * ## Why a ratio is worth its own construction
 *
 * Because it is one of the very few metric-sounding statements that **survives projection**.
 * An affine map preserves ratios along a line, so "M is two thirds of the way along AB" is as
 * true of the drawing as it is of the solid — unlike a distance, an angle or an area, every
 * one of which the projection is free to change. That makes it safe to state on a figure of a
 * solid, and it is why the exercise book leans on it so heavily.
 *
 * `t` is measured from `a`: 0 is `a`, 1 is `b`, ½ is the midpoint. Values outside that range
 * are allowed and land on the extensions, which is what "trên tia đối của AB" asks for.
 */
export interface RatioPoint {
  readonly op: 'ratio-point';
  readonly a: EntityId;
  readonly b: EntityId;
  readonly t: number;
}

/**
 * A solid, as the thing its corners belong to.
 *
 * ## Why this exists at all
 *
 * Because the figure of a solid is a *projection*, and the page has thrown away the coordinate
 * that decides what covers what. The first version worked that out while the script ran and
 * baked the answer into each line's style — which is correct exactly once. Drag a corner and
 * the shape moves while the dashes stay where they were, and ADR-0003 has a name for that
 * mistake: a source must say how an entity is *determined*, never what it currently looks like.
 *
 * So the solid keeps its own description — what shape, how big, where — and everything else is
 * derived from it. Nothing is drawn for this entity itself; its corners and edges are separate
 * objects, which is what lets a problem name them.
 */
/**
 * A plane cutting a sphere: how far from the centre, and how far off horizontal.
 *
 * An object rather than two arrays side by side. This file already carries a warning about
 * exactly that shape — a ring stored flat has to be paired up again by every reader, and doing
 * that in two places is how two readers come to disagree about which number is which.
 */
export interface SphereSection {
  /** Signed distance from the centre. Positive is above. */
  readonly at: number;
  /** Degrees the plane leans off horizontal. Absent is level, which is nearly every figure. */
  readonly tilt?: number;
}

export interface SolidFigure {
  readonly op: 'solid-figure';
  readonly solid: 'prism' | 'pyramid' | 'cone' | 'cylinder' | 'sphere';
  /** Where the centre of the solid sits. Dragging this moves the whole figure. */
  readonly center: EntityId;
  readonly width: number;
  readonly height: number;
  readonly depth: number;
  /** Corners in one ring, for a prism or a pyramid. Ignored by the round solids. */
  readonly corners: number;
  /**
   * Planes cutting a **sphere**, each as its signed distance from the centre.
   *
   * "Mặt phẳng (P) cách tâm O một khoảng d cắt mặt cầu theo một đường tròn" — the small ellipse
   * a teacher draws above or below the equator. Only the distance is stored: the circle's radius
   * is `√(R² − d²)`, which is the theorem the problem is about, and keeping both numbers would
   * let a figure be saved that the theorem forbids.
   *
   * Numbers rather than entity ids, like the sizes beside them: a section is a measurement of
   * this solid, not a reference to anything the document could delete out from under it.
   */
  readonly sections?: readonly SphereSection[];
  /** Base corners the apex stands over: none for the centre, one, or two for an edge midpoint. */
  readonly foot: readonly number[];
  /**
   * The base corner whose angle the problem states, and the angle, in degrees.
   *
   * Without it a three-cornered base is a regular triangle and a four-cornered one is a
   * rectangle, and a great many problems say neither: "đáy là tam giác vuông tại B", "đáy ABCD
   * là hình thoi cạnh a, góc BAD = 60°". There was no way to draw either. The figure came out
   * equilateral or rectangular, an `anglemark` at the corner correctly reported the angle it
   * found, and the drawing contradicted its own caption — with nothing to tell a reader that the
   * shape was the approximation and the words were the truth.
   *
   * The two sides meeting there are the size numbers: back to the previous corner is `width`, on
   * to the next is `depth`. So this says only *where* they meet and at what angle. Kept as two
   * flat numbers rather than a pair, because the file codec rounds numbers it can see and would
   * write a nested object through untouched.
   *
   * Both absent or both present. A corner with no angle has nothing to say, and an angle with no
   * corner has nowhere to be.
   */
  readonly baseAngleAt?: number;
  readonly baseAngle?: number;
  /**
   * The far parallel side of a trapezium base, in the same units as `width` and `depth`.
   *
   * Absent means the two parallels are equal, which is a parallelogram — so this single number is
   * the whole difference between "đáy ABCD là hình bình hành" and "đáy ABCD là hình thang", and
   * the shape is built by one construction either way rather than two that could disagree.
   */
  readonly baseFar?: number;
  /**
   * How far along the parallels the far side of a trapezium starts.
   *
   * Zero leaves it flush against the leg, which with a right angle is "hình thang vuông tại A và
   * D"; half the difference between the two parallels centres it and the legs come out mirror
   * images, which is "hình thang cân". One number rather than a flag, because the geometry only
   * ever wanted a distance and a flag would have to be turned back into one somewhere.
   */
  readonly baseFarOffset?: number;
  /**
   * The base given corner by corner, as x and z in the base plane, flat.
   *
   * The escape hatch for a base no clause describes. Present instead of the others, never
   * alongside: it says the shape outright, so `width` and `depth` are read back off it rather
   * than stated, and there is nothing left for an angle to add.
   *
   * Flat numbers because the file codec rounds what it can see and would write a list of pairs
   * through untouched — and because a base is a ring, whose pairing is a convention either way.
   */
  readonly baseCorners?: readonly number[];
}

/**
 * A corner of a solid, by position in its own numbering.
 *
 * Derived rather than free, and that is the point: it cannot be dragged out of the solid it
 * belongs to. One corner of a cube moved on its own is not a cube, and a figure that let you do
 * it would be one that stopped meaning what it says without saying so.
 */
export interface SolidVertex {
  readonly op: 'solid-vertex';
  readonly solid: EntityId;
  readonly index: number;
}

/**
 * A plane, named the way a problem names one: by three of the figure's points.
 *
 * ## Why a plane is an object here at all
 *
 * "đó đã là best practice cho ứng dụng vẽ hình chưa, đã đúng với cách dựng hình trong thực tế
 * của giáo viên chưa?" It was not. A plane was a *style name*, and a section was drawn by typing
 * its answer as a polygon — which works only for someone who has already solved the problem, and
 * is unchecked: four points nowhere near a common plane drew as a convincing shaded quadrilateral.
 *
 * A teacher names a plane, asks for its section, and asks where two planes meet. Two of those the
 * program could not answer. Now it can, and the answers are *computed*, so they stay right when
 * the solid is turned or resized — which is the whole of ADR-0003 applied to the one construction
 * that had been left out of it.
 *
 * ## Why the solid is named too
 *
 * Because a plane is unbounded and a figure is not: what a reader sees is the plane's **section**
 * with the solid, and there is no such thing without saying which solid. Naming it also puts the
 * dependency in the graph, so turning the solid re-cuts the section instead of leaving a shape
 * floating where the old one was.
 */
export interface PlaneSource {
  readonly op: 'plane';
  readonly solid: EntityId;
  readonly a: EntityId;
  readonly b: EntityId;
  readonly c: EntityId;
}

/**
 * A plane through a point, parallel to another plane.
 *
 * "Mặt phẳng qua M song song với (ABC)" — the second commonest way a paper names one, and not
 * expressible as three points without solving for them first.
 */
export interface PlaneParallelSource {
  readonly op: 'plane-parallel';
  readonly solid: EntityId;
  readonly through: EntityId;
  readonly to: EntityId;
}

/**
 * The frame a figure in space is drawn through, when there is no solid to borrow one from.
 *
 * ## Why it exists
 *
 * *"Giáo viên đứng lớp chỉ có mỗi cái bảng đen… không lẽ phải vẽ 1 khối ra trước?"* — and the
 * answer was yes, which was a gap rather than a rule. A blackboard plane is a leaning
 * parallelogram with `(P)` written on it, and the whole chapter on quan hệ song song and quan hệ
 * vuông góc is drawn that way, with no body anywhere in it. Every plane construction here needed
 * a solid, so none of those figures could be built.
 *
 * A plane still has to be *drawn*, and drawing anything worked out in space means projecting it.
 * A solid carries its own projection; a plane on its own has none to carry. This is that
 * projection, held once for the whole figure.
 *
 * ## Why it is shared rather than per-plane
 *
 * Because two planes drawn through different projections make a figure that is **geometrically
 * false and looks perfectly normal** — the exact failure ADR-0003 exists to prevent. Holding the
 * projection on each plane would let a user change one and quietly break the drawing. Held here,
 * there is nothing to keep in step.
 *
 * ## Why it is invisible, and why nobody has to make it
 *
 * It has no shape of its own — it is a *viewpoint*, and a viewpoint is not a thing on the page.
 * The tool creates it on the first click and `unreachableParentsOf` sweeps it away when the last
 * plane resting on it is deleted, which is the same life a solid's hidden anchor already has.
 */
export interface SpaceSource {
  readonly op: 'space';
  /** Where the figure's origin lands on the page. Hidden, and made by the tool. */
  readonly center: EntityId;
  readonly rotation: Euler;
  readonly projection: ProjectionKind;
}

/**
 * A plane in its own right, drawn the way a hand draws one: a leaning parallelogram.
 *
 * ## How it differs from `plane`
 *
 * `plane` is a **section** — where a plane cuts a solid — and its shape is the solid's answer.
 * This is a plane with nothing to cut, so its shape is a *choice*: a parallelogram as wide as the
 * author found convenient, standing for a surface that really runs on forever. Two objects, one
 * word, and neither is a weakened version of the other.
 *
 * ## The two numbers
 *
 * `tilt` turns it about the frame's x axis, so two planes sharing a frame and differing in tilt
 * meet along that axis — which is "hai mặt phẳng cắt nhau theo giao tuyến d", and the giao tuyến
 * is then *computed* rather than drawn by eye.
 *
 * `size` is half the parallelogram's width. It changes what is drawn and nothing that is true:
 * the plane it stands for is the same plane at any size, and every angle, foot and intersection
 * comes out the same. A reader is entitled to know that the edge of the wash is not the edge of
 * anything.
 */
export interface PlaneFreeSource {
  readonly op: 'plane-free';
  readonly space: EntityId;
  /** Degrees about the frame's x axis. Zero is level. */
  readonly tilt: number;
  /** Half the drawn width, in world units. Appearance only. */
  readonly size: number;
}

/**
 * A point lying on a free plane, held as coordinates in that plane's own axes.
 *
 * ## Why this is what unlocks the rest
 *
 * Everything else about a plane is named through points: a line *in* a plane is a segment between
 * two of them, an axis to turn about is two of them, "mặt phẳng qua M song song với (P)" needs one.
 * Without these, a figure with no solid could show planes and nothing else — no lines, no names,
 * no constructions. With them, every plane construction already built starts working on blackboard
 * figures without being changed.
 *
 * ## Why `(u, v)` and not a page position
 *
 * Because the point is *on the plane* and must stay there when the plane tilts or the frame turns
 * — the same reason a `locus-point` stores a parameter along its host rather than a coordinate.
 * A page position would be right once and then quietly wrong, which ADR-0003 forbids in as many
 * words: a source says how an entity is determined, never what it currently looks like.
 *
 * Clamped to the drawn parallelogram when dragged, so a point cannot end up outside the wash it is
 * supposed to lie on — a figure reading "M does not belong to (P)" while M does.
 */
export interface PlanePointSource {
  readonly op: 'plane-point';
  readonly plane: EntityId;
  readonly u: number;
  readonly v: number;
}

/**
 * A plane turned about a line lying in it, by a stated angle.
 *
 * "Mặt phẳng qua AB hợp với (ABCD) một góc 60°" — the third way a paper names a plane, and the
 * one this editor could not say at all. Every other plane construction here describes a
 * **position**; this is the only one that describes an **angle**, which is what a problem about a
 * pair of planes is actually about.
 *
 * ## Why a turn and not an angle
 *
 * "A plane at 60° to (ABCD)" names a whole cone of planes, so the figure would have to pick one,
 * and a plausible-looking guess is the one thing ADR-0003 forbids. A turn names exactly one: this
 * plane, this axis, this many degrees. It is also the form GeoGebra settled on — its sixty 3D
 * commands include no plane-at-an-angle, only `Rotate(object, angle, axis)` — where the CAD form
 * has left Onshape's users unable to establish for nine years which reference their angle was
 * measured from.
 *
 * ## Why the number is stored rather than the result
 *
 * Because 60 is the *statement*. Solving for three points and storing those would leave nothing in
 * the file saying sixty, so turning the solid, changing its projection or dragging a corner would
 * silently make the figure answer a different question. Held this way, the angle is re-established
 * from the current geometry every time anything moves.
 *
 * `a` → `b` directs the axis, and that direction **is** the sign: naming the two points the other
 * way round turns the plane the other way, which is exactly `-degrees`. One rule, no flag, and no
 * second answer for a reader to wonder about.
 */
export interface PlaneTurnSource {
  readonly op: 'plane-turn';
  readonly solid: EntityId;
  /** The plane being turned. */
  readonly from: EntityId;
  readonly a: EntityId;
  readonly b: EntityId;
  readonly degrees: number;
}

/**
 * The angle between two planes, marked where it actually is.
 *
 * ## Why it cannot be an ordinary angle mark
 *
 * Because the dihedral is **not** the angle between any two edges you can see. It is the angle
 * between two rays drawn from a point on the giao tuyến, each lying in one plane and each
 * perpendicular to that line — and finding those rays is precisely the step a student is being
 * taught. Marking `∠SMO` by hand works only when the author has already done the construction and
 * knows where `M` goes, which is what the corpus's cone figure does: `phi = anglemarkbig S M O`,
 * correct because the person writing it knew, and reproducible by nobody else.
 *
 * So the mark is derived. The line, the point on it, both perpendiculars and the angle between
 * them are computed from the two planes, and every one of them follows when the solid turns.
 *
 * ## Why it evaluates to an ordinary `angle-mark`
 *
 * Because that is what it *is* on the page: an arc between two directions at a vertex. Giving it
 * a value kind of its own would have meant teaching the painters, the exporters, the hit test and
 * the spatial index a new word to end up drawing the arc they already draw.
 */
export interface DihedralMarkSource {
  readonly op: 'dihedral-mark';
  readonly solid: EntityId;
  readonly a: EntityId;
  readonly b: EntityId;
  /** 1, 2 or 3 arcs. Ignored when the angle is right. */
  readonly ticks: number;
  /** How large the arc is drawn, as a multiple of the ordinary size. Absent means one. */
  readonly size?: number;
  /**
   * Writes the measured angle beside the mark.
   *
   * Off by default, and that is the schoolbook convention rather than an oversight: in most
   * problems the dihedral is the thing being *asked for*, so printing it on the figure would
   * hand over the answer. A teacher who wants it stated turns it on.
   */
  readonly showValue: boolean;
  /** Where the user dragged the number to, as a displacement from where it would sit alone. */
  readonly valueOffset?: Vec2;
}

/**
 * Where two planes meet: the *giao tuyến*, as the segment of it inside the solid.
 *
 * A segment rather than a line, because the line runs off the paper and a figure wants the piece
 * that is in the body — `(SAC) ∩ (SBD)` is `SO`, the height of the pyramid, drawn in half the
 * problems in the book.
 */
export interface PlaneMeetSource {
  readonly op: 'plane-meet';
  readonly solid: EntityId;
  readonly a: EntityId;
  readonly b: EntityId;
}

/**
 * The foot of the perpendicular from a point to a plane.
 *
 * The written reference used to say this was not needed — "there is no `foot` onto a *plane*,
 * and there does not need to be" — which held only for as long as a plane was a style name. It
 * is what every "khoảng cách từ S đến (ABCD)" is about, and what the height of a pyramid is:
 * `H = foot S base`, then the segment `SH`.
 */
export interface PlaneFootSource {
  readonly op: 'plane-foot';
  readonly solid: EntityId;
  readonly plane: EntityId;
  readonly from: EntityId;
}

/**
 * A point on a round solid's base circle, at an angle measured in the base plane.
 *
 * Its own construction rather than a locus point on the drawn ellipse, because the angle is a
 * statement about the *solid* — "hai đường sinh SA và SB" says where A and B sit on the circle,
 * not where the projection happens to put them.
 */
export interface SolidRimPoint {
  readonly op: 'solid-rim-point';
  readonly solid: EntityId;
  /** Which base circle: 0 is the lower one, 1 the upper on a cylinder. */
  readonly ring: number;
  readonly degrees: number;
}

/**
 * The foot of the perpendicular from a point to a line, computed in space.
 *
 * `projected-point` finds it on the page, which is a different point: an affine projection does
 * not preserve perpendicularity. All three must belong to the same solid, because that is what
 * gives them positions to be perpendicular in.
 */
export interface SolidFoot {
  readonly op: 'solid-foot';
  /** The solid all three belong to: what gives them positions to be perpendicular in. */
  readonly solid: EntityId;
  readonly from: EntityId;
  readonly a: EntityId;
  readonly b: EntityId;
}

export interface SegmentMidpoint {
  readonly op: 'segment-midpoint';
  readonly segment: EntityId;
}

/**
 * One intersection of two curves.
 *
 * `index` selects which one when there are several. It is an index into the
 * deterministically-ordered result of the corresponding routine in
 * `domain/geometry/intersect` — which is precisely why that module documents its
 * ordering as part of its contract.
 */
export interface Intersection {
  readonly op: 'intersection';
  readonly a: EntityId;
  readonly b: EntityId;
  readonly index: number;
}

export interface ProjectedPoint {
  readonly op: 'projected-point';
  readonly point: EntityId;
  readonly onto: EntityId;
}

export interface ReflectedPoint {
  readonly op: 'reflected-point';
  readonly point: EntityId;
  readonly mirror: EntityId;
}

export interface RotatedPoint {
  readonly op: 'rotated-point';
  readonly point: EntityId;
  readonly center: EntityId;
  readonly angle: number;
}

export interface TranslatedPoint {
  readonly op: 'translated-point';
  readonly point: EntityId;
  readonly vector: EntityId;
}

export interface ScaledPoint {
  readonly op: 'scaled-point';
  readonly point: EntityId;
  readonly center: EntityId;
  readonly factor: number;
}

export interface TriangleCenter {
  readonly op: 'circumcenter' | 'incenter' | 'centroid' | 'orthocenter';
  readonly a: EntityId;
  readonly b: EntityId;
  readonly c: EntityId;
}

export interface CircleCenter {
  readonly op: 'circle-center';
  readonly circle: EntityId;
}

/* ------------------------------------------------------------------------ linear */

export interface SegmentSource {
  readonly op: 'segment';
  readonly a: EntityId;
  readonly b: EntityId;
}

export interface RaySource {
  readonly op: 'ray';
  readonly origin: EntityId;
  readonly through: EntityId;
}

export interface LineSource {
  readonly op: 'line';
  readonly a: EntityId;
  readonly b: EntityId;
}

export interface VectorSource {
  readonly op: 'vector';
  readonly from: EntityId;
  readonly to: EntityId;
}

/** `reference` may be any linear entity; the result passes through `through`. */
export interface ParallelLine {
  readonly op: 'parallel-line';
  readonly reference: EntityId;
  readonly through: EntityId;
}

export interface PerpendicularLine {
  readonly op: 'perpendicular-line';
  readonly reference: EntityId;
  readonly through: EntityId;
}

export interface PerpendicularBisector {
  readonly op: 'perpendicular-bisector';
  readonly a: EntityId;
  readonly b: EntityId;
}

/** The bisector of angle `a`–`vertex`–`c`, as a ray from the vertex. */
export interface AngleBisector {
  readonly op: 'angle-bisector';
  readonly a: EntityId;
  readonly vertex: EntityId;
  readonly c: EntityId;
}

/** A tangent from an external point; `index` selects between the two. */
export interface TangentLine {
  readonly op: 'tangent-line';
  readonly conic: EntityId;
  readonly from: EntityId;
  readonly index: number;
}

/**
 * The tangent to a curve at a given `x`, which is a different construction from the one above.
 *
 * ## Why it is its own op
 *
 * `tangent-line` answers "from this point outside the circle", and the two tangents it chooses
 * between are found by intersecting circles. This answers "at this abscissa", there is exactly
 * one of them, and it is found by differentiating. Nothing but the English word is shared, and
 * folding them together would mean an evaluator that branches on what kind of thing its parent
 * turned out to be — which is how a construction ends up silently doing the wrong one.
 *
 * ## Why an `x` and not a point on the curve
 *
 * Because that is how the problem states it: "viết phương trình tiếp tuyến của (C) tại điểm có
 * hoành độ x₀ = 2". A point would have to be constrained to the curve to be meaningful, and a
 * free point *near* the curve would give a tangent to nothing — a line drawn confidently through
 * empty space at a slope that answers no question.
 */
export interface GraphTangent {
  readonly op: 'graph-tangent';
  /** The curve it touches. */
  readonly graph: EntityId;
  /** The abscissa of the point of contact, in the graph's own units. */
  readonly at: number;
}

/* ------------------------------------------------------------------------ conics */

export interface CircleThrough {
  readonly op: 'circle-through';
  readonly center: EntityId;
  readonly through: EntityId;
}

export interface CircleRadius {
  readonly op: 'circle-radius';
  readonly center: EntityId;
  readonly radius: number;
}

export interface CircleThreePoints {
  readonly op: 'circle-3-points';
  readonly a: EntityId;
  readonly b: EntityId;
  readonly c: EntityId;
}

export interface ArcThreePoints {
  readonly op: 'arc-3-points';
  readonly a: EntityId;
  readonly b: EntityId;
  readonly c: EntityId;
}

export interface ArcCenter {
  readonly op: 'arc-center';
  readonly center: EntityId;
  readonly start: EntityId;
  readonly end: EntityId;
  readonly counterclockwise: boolean;
}

export interface EllipseSource {
  readonly op: 'ellipse';
  readonly center: EntityId;
  readonly rx: number;
  readonly ry: number;
  readonly rotation: number;
}

/** The classic string-and-two-pins definition: the locus with constant focal sum. */
export interface EllipseFoci {
  readonly op: 'ellipse-foci';
  readonly f1: EntityId;
  readonly f2: EntityId;
  readonly through: EntityId;
}

/* ---------------------------------------------------------------------- polygons */

export interface PolygonSource {
  readonly op: 'polygon';
  readonly vertices: readonly EntityId[];
}

/**
 * A polygon combined from two others.
 *
 * Derived, so it re-evaluates when either operand moves — dragging a vertex of one square
 * reshapes the union live, which is the whole reason it is a construction rather than a
 * one-off command that bakes vertices.
 *
 * Evaluates to nothing when the result is not a single ring. `Evaluated` carries one ring
 * per polygon, so a result with a hole or two disjoint pieces has no faithful
 * representation yet; producing the largest ring instead would silently discard part of
 * the answer, which is worse than producing nothing the user can see coming.
 */
export interface PolygonBoolean {
  readonly op: 'polygon-boolean';
  readonly operation: 'union' | 'intersection' | 'difference';
  readonly a: EntityId;
  readonly b: EntityId;
}

export interface RegularPolygonSource {
  readonly op: 'regular-polygon';
  readonly center: EntityId;
  readonly vertex: EntityId;
  readonly sides: number;
}

/* ------------------------------------------------- free-form drawing (not derived) */

/**
 * Freehand and polyline geometry drawn directly rather than constructed.
 *
 * These carry raw coordinates because there is nothing to derive them from. They are
 * still entities — selectable, styleable, undoable — but they have no parents and never
 * participate in the DAG as anything other than leaves.
 */
export interface FreePolyline {
  readonly op: 'free-polyline';
  readonly points: readonly Vec2[];
  readonly closed: boolean;
}

export interface FreeEllipse {
  readonly op: 'free-ellipse';
  readonly center: Vec2;
  readonly rx: number;
  readonly ry: number;
  readonly rotation: number;
}

/**
 * A solid, anchored to a point in the plane.
 *
 * Derived from that point, so it moves with it and belongs in the DAG like anything else.
 * Rotation and projection live here rather than on the document: showing the same shape
 * under two projections side by side is a teaching move, and a global setting would forbid
 * it.
 */
export interface SolidSource {
  readonly op: 'solid';
  readonly solid: SolidKind;
  /** The point the solid's centre sits on. */
  readonly center: EntityId;
  readonly width: number;
  readonly height: number;
  readonly depth: number;
  /** Base polygon sides, for prisms and pyramids. */
  readonly sides: number;
  /** Top radius as a fraction of the base, for a frustum. */
  readonly taper: number;
  readonly rotation: Euler;
  readonly projection: ProjectionKind;
}

/**
 * A graph of `y = f(x)`.
 *
 * ## Why the range is stored rather than taken from the camera
 *
 * The obvious design samples across whatever is on screen, so the curve always fills the view.
 * It is also wrong here, for the same reason an angle mark's bounds are not allowed to depend
 * on the zoom: a document's geometry may not change because somebody scrolled. An exported
 * PDF would differ from the screen it was exported from, and two people opening the same file
 * would see different curves.
 *
 * So the domain is a property of the graph, defaulting to a range that suits a school figure
 * and editable when it does not. What the user sees is what the file says.
 */
/**
 * A pair of coordinate axes.
 *
 * ## Why this is an entity rather than part of the grid
 *
 * The grid is a drawing aid: adaptive, tied to the zoom, and deliberately absent from every
 * export. Axes are the opposite on all three counts. They are *part of the figure* — a curve
 * with no axes says nothing at all about where the origin is or what one unit means, and an
 * exported graph without them is a squiggle. So they live in the document, carry a style, get
 * listed among the objects, and export like anything else.
 *
 * ## Why they are not drawn by the graph itself
 *
 * The tempting shortcut is for each graph to draw its own axes. Two graphs would then draw two
 * sets on top of each other, at whatever thickness the second one happened to be styled with.
 * And axes are useful with no graph at all — plotting points, reading coordinates, drawing a
 * transformation — which a graph-owned pair could never provide.
 */
export interface AxesSource {
  readonly op: 'axes';
  /**
   * World units per mathematical unit.
   *
   * Must match the graphs it accompanies, which is why `addFunctionGraph` creates the axes
   * itself rather than leaving the user to line two numbers up by hand.
   */
  readonly scale: number;
  /** How far the x axis reaches, in mathematical units, each way from the origin. */
  readonly extent: number;
  /**
   * How far the **y** axis reaches. Absent means the same as `extent`.
   *
   * Separate because one number cannot frame most functions. `y = x³ − 3x + 1` over `x ∈ [−2.5,
   * 2.5]` climbs to ±9: a square window either chops the curve into fragments or, made tall
   * enough to hold it, leaves the curve a thin squiggle down the middle of an empty page. A
   * textbook draws that cubic in a tall narrow frame, and this is what lets the figure be one.
   *
   * Optional so every axis pair already saved, and every geometry figure that wants a plain
   * square pair, is unchanged.
   */
  readonly extentY?: number;
  /**
   * The middle of the frame, in mathematical units. Absent means the origin.
   *
   * ## Why a frame may be off-centre
   *
   * Because most problems are. `y = x² − 4x + 3` over `x ∈ [−1; 5]` needs to reach 5 to the
   * right and 1 to the left, and a frame symmetric about the origin has to reach 5 both ways —
   * so **half the picture is blank paper** and the curve is drawn at half the size it could
   * be. The same waste, worse, on a miền nghiệm: a system in the first quadrant is drawn in a
   * frame three quarters of which no inequality has anything to say about.
   *
   * A textbook simply draws the part of the plane the problem is about.
   *
   * ## Why a centre rather than four reaches
   *
   * `extent` keeps its meaning — half the width — and one optional field says where that half
   * width is measured from. Four separate reaches would mean a fallback chain (`extentDown ??
   * extentY ?? extent`) with two ways to write the same frame, which is the shape of thing this
   * file has been burned by before.
   *
   * Absent is the origin, so every axis pair already saved, every geometry figure that wants a
   * plain square pair, and `axes 10` in the script language are all unchanged and need no
   * migration.
   *
   * The **axes themselves** are still drawn along `x = 0` and `y = 0`, wherever the frame is —
   * they are the axes, not the edges of the paper. A frame that excluded the origin would draw
   * neither, so whatever chooses one is expected to keep the origin inside it.
   */
  readonly center?: Vec2;
  /** Mathematical units between tick marks on the x axis. */
  readonly step: number;
  /**
   * Units between ticks on the **y** axis. Absent means the same as `step`.
   *
   * Separate because one number cannot keep both axes readable once they have different extents.
   * A step chosen to suit an axis reaching 37 units puts nothing at all on one reaching 1; a step
   * chosen for the short one draws thirty-seven numbers down the long one. The figure that made
   * it plain carried 37 numbers on its y axis against 5 on its x.
   */
  readonly stepY?: number;
  /**
   * World units per mathematical unit on the **y** axis. Absent means the same as `scale`.
   *
   * ## Why a graph may have unequal units and a geometry figure may not
   *
   * On equal axes a circle is a circle and a square is a square, and for a figure about *shapes*
   * that is not negotiable. So this is absent by default and stays absent: nothing sets it but
   * the two paths that fit axes to a **function**, and a geometry figure never goes near them.
   *
   * For a function it is the difference between a figure and a column. `y = −x²` over `[−10; 10]`
   * is a hundred units tall and twenty wide; with one unit for both axes there is no framing of
   * it that is not a tower, and the only lever left is to cut more of the curve away. A textbook
   * does not cut the curve — it writes a different number on the y axis. This is that number.
   *
   * Absent rather than defaulted to 1 for the usual reason: every file already saved, and every
   * pair of axes a geometry figure asks for, means exactly what it meant before.
   */
  readonly scaleY?: number;
  /** Whether the ticks are numbered. Off for a bare pair of axes in a geometry figure. */
  readonly numbered: boolean;
}

/**
 * The window a graph gets when nothing says otherwise, and the size it is drawn at.
 *
 * Here rather than in the tool that first needed them, because three callers have to agree:
 * the toolbar's `addFunctionGraph`, the written-construction language's `graph`, and the
 * `axes` those two are read against. **A graph and its axes drawn at different scales is a
 * figure that lies** — the curve crosses at 2 and the tick says 3 — and two copies of the
 * number in two layers is exactly how that happens.
 *
 * Ten either side is what a textbook axis shows. Forty world units to the mathematical unit is
 * chosen against the rest of a drawing rather than against the screen: a triangle in this
 * editor is a few hundred units across, so a graph eight hundred wide sits beside one at a
 * comparable size.
 */
export const DEFAULT_GRAPH_RANGE = { from: -10, to: 10 } as const;
export const DEFAULT_GRAPH_SCALE = 40;

export interface FunctionGraph {
  readonly op: 'function-graph';
  /** The formula as the user typed it, so it round-trips and can be edited back. */
  readonly expression: string;
  /** The range of `x`, in the graph's own units. */
  readonly from: number;
  readonly to: number;
  /**
   * World units per mathematical unit on the x axis.
   *
   * A graph needs a scale of its own because the canvas has none: the grid is adaptive, so
   * there is no fixed number of world units to a square, and `x` from −10 to 10 drawn at one
   * unit each would be twenty units wide beside a triangle three hundred units across.
   */
  readonly scale: number;
  /**
   * The same for the y axis. Absent means equal units, which is the default and stays the default.
   *
   * ## The argument this field had to answer
   *
   * This used to read "applied to both axes equally, and that is not negotiable: a parabola
   * stretched sideways is not a parabola, and a circle drawn from `sqrt(1-x^2)` would come out an
   * ellipse." Half of that is exactly right and the other half is a different figure.
   *
   * The half that is right is about **shape**. When a reader is meant to *see* that something is
   * a circle, unequal units make the picture lie, and no labelling on the axes repairs it. That
   * is why this is absent unless something deliberately sets it, why nothing sets it but the two
   * paths that fit axes to a function, and why a geometry figure never acquires one.
   *
   * The half that is a different figure is about **value**. `y = −x²` over `[−10; 10]` is a
   * hundred units tall and twenty wide. Every textbook in the syllabus draws it with a compressed
   * y axis, and the picture is not a lie: the reader takes values off the numbers on the axis,
   * not off the aspect of the page. Refusing meant the only lever left was to cut the curve, and
   * cutting a quarter of `−x²` away to preserve a squareness nobody was reading is the worse
   * trade — as the 944 × 3031 export that prompted this made plain.
   *
   * So: equal by default, unequal when a function would otherwise be a column, never silently.
   */
  readonly scaleY?: number;
  /**
   * Where the formula is written, as a displacement from where it would go on its own.
   *
   * The label is placed at the first point of the curve, which is the right guess and only a
   * guess: on a figure carrying two curves, or a curve that starts where the axes are, it lands
   * on top of something. A reader cannot be asked to work out which of two overlapping formulas
   * belongs to which line, so the placement has to be correctable.
   *
   * A displacement rather than a position, so the label keeps following its curve — edit the
   * formula, move the graph, refit the axes, and a label nudged clear of the y axis stays clear
   * of it. The same shape as `angle-mark`'s `valueOffset` and for the same reason, and dragged
   * through the same branch of `SelectTool.translateResponse`.
   *
   * Absent means nobody has moved it, which is what every file written before this says.
   */
  readonly labelOffset?: Vec2;
}

/**
 * The bảng biến thiên of a graph.
 *
 * ## Why it names the graph rather than carrying a formula
 *
 * It could hold its own copy of `x^3-3x` and be independent of everything. Then editing the
 * formula would leave a table beside the new curve that describes the old one — and it would
 * look right, because a variation table gives a reader no way to check it against the picture.
 * A wrong table that looks right is the worst thing this file could produce.
 *
 * Naming the graph makes it a derived object like any other in this editor: change the
 * formula, and the table follows in the same pass that redraws the curve. It also means
 * deleting the graph takes the table with it, which is the correct answer to "what is this a
 * table of" once the answer is nothing.
 *
 * ## Why it carries a position and the solid does not
 *
 * A solid hangs off a point because it *is* geometry and belongs somewhere in the figure. A
 * table is a caption: it is placed on the page, beside the curve or under it, wherever there
 * is room. Making the user create an anchor point for it first would be ceremony, so it holds
 * its own corner and is dragged directly.
 */
export interface VariationTableSource {
  readonly op: 'variation-table';
  /** The graph this tabulates. */
  readonly graph: EntityId;
  /** The top-left corner, in world units. */
  readonly at: Vec2;
}

/**
 * The convex solid on a set of points that know where they are in space.
 *
 * ## Why it names points rather than carrying coordinates
 *
 * Because the points that matter already exist. A corner of a solid, the midpoint of two corners,
 * a point dividing an edge, the foot of a perpendicular — every one of them carries its position
 * in space, and a problem that says "khối tứ diện ACB'D' của hình lập phương" is naming four of
 * them. Asking for the coordinates again would be asking a teacher to compute what the figure has
 * already worked out, and to keep the two in step by hand afterwards.
 *
 * Naming them also makes it derived like everything else here: move the cube and the tetrahedron
 * cut out of it moves with it.
 *
 * ## Why the faces are not stated
 *
 * They follow from the corners for a convex body — see `hullOf`. A syntax that made the writer
 * list them would be a worse way of saying the same thing and wrong in a way nothing catches: one
 * mistyped face draws a solid with a hole in it.
 *
 * A body that is *not* convex cannot be said this way, and that is the honest limit of this
 * construction rather than an oversight. The hidden-line cutter handles non-convex bodies now, so
 * what is missing is only a way to state the faces — for the day a figure needs one.
 */
/**
 * The piece of a solid a plane leaves, named by a corner it keeps.
 *
 * ## Why a corner rather than a side
 *
 * Because that is how the problem says it: "phần chứa đỉnh S", "khối đa diện chứa điểm A". A
 * plane divides space in two and nothing about the plane says which half is wanted — `above` and
 * `below` would have to be defined against the plane's own normal, which depends on the order its
 * three points were named and is therefore invisible to the person reading the figure.
 *
 * Naming a corner is unambiguous, survives the solid being moved or resized, and reads back as
 * the sentence it came from.
 *
 * ## Why it is a hull rather than a clipping
 *
 * The piece a plane leaves of a **convex** body is convex, and its corners are exactly the
 * original corners on the kept side together with the corners of the section. So there is nothing
 * to clip: collect those points and take their hull. A body that is not convex cannot be cut this
 * way, and that is the honest limit — the same one `polyhedron` has.
 */
export interface SolidCutSource {
  readonly op: 'solid-cut';
  /** The body being cut. Found from the plane rather than stated, as a plane's own solid is. */
  readonly solid: EntityId;
  /** The plane that cuts it, whose section supplies the new face. */
  readonly plane: EntityId;
  /** A corner of the body that the wanted piece contains. */
  readonly keeps: EntityId;
}

export interface PolyhedronSource {
  readonly op: 'polyhedron';
  /** Four or more points, each of which has to know where it is in space. */
  readonly corners: readonly EntityId[];
}

/**
 * The notable points of a graph, marked.
 *
 * ## Why it names the graph instead of holding coordinates
 *
 * The same reason the variation table does, and it is the whole reason this is one object rather
 * than a handful of points. Marks placed as plain points are correct when they are made and
 * silently wrong from the next edit onwards: retype `x^2` as `x^3` and the dots stay where the
 * parabola's turning point used to be, labelled with its coordinates, sitting on a curve that
 * has none there. A figure that is wrong and looks right is the worst thing this file can make.
 *
 * Naming the graph makes them derived like everything else in the editor: the curve changes and
 * they follow in the same pass, and deleting the curve takes them with it.
 *
 * The cost is real and worth stating: they are one object in the list, so a teacher cannot
 * delete a single dot. What they can do instead is choose which *kinds* are shown, which is the
 * thing anyone actually wants — and a dot left behind on the wrong curve is not fixable at all.
 *
 * ## Why guides are a flag rather than a second statement
 *
 * Because a dashed line to each axis and a coordinate written beside the dot are two ways of
 * saying one thing, and a textbook does one or the other. Drawing both doubles the ink to
 * repeat itself. Which one is right depends on the question: "tìm f(2) dựa vào đồ thị" wants the
 * reader to run their eye to the axis, and printing `(2; -1)` beside the dot answers the
 * question the figure was supposed to ask.
 */
export interface GraphMarks {
  readonly op: 'graph-marks';
  /** The curve whose notable points these are. */
  readonly graph: EntityId;
  /**
   * Which of them to show, as the domain names them.
   *
   * Empty means none, which is a legitimate state while a teacher is unticking boxes — the
   * object stays and draws nothing rather than deleting itself under them.
   */
  readonly kinds: readonly string[];
  /** Dashed lines to both axes with the value written on each, instead of a coordinate label. */
  readonly guides: boolean;
}

export interface ImageSource {
  readonly op: 'image';
  readonly position: Vec2;
  readonly width: number;
  readonly height: number;
  readonly href: string;
}

/* -------------------------------------------------------------------- annotation */

/**
 * A text label.
 *
 * `anchor` ties the label to an entity so it follows when that entity moves; `offset` is
 * the displacement from the anchor. Unanchored labels store an absolute position in
 * `offset` instead — one field, two meanings, disambiguated by `anchor`.
 */
export interface TextSource {
  readonly op: 'text';
  readonly anchor: EntityId | null;
  readonly offset: Vec2;
  readonly content: string;
  readonly latex: boolean;
}

/**
 * The mark on an angle: a right-angle square, or one to three arcs.
 *
 * A construction over the three points, not a decoration placed on the page. That is the
 * whole design, and the consequence is the point of it: **the mark tells the truth about the
 * angle it is on**. Drag the figure until the angle stops being right and the square becomes
 * an arc, because a right-angle square on an 87° angle is a diagram that lies — which in a
 * geometry lesson is the one thing a diagram must never do.
 *
 * `ticks` is how many arcs are drawn, which is the notation for "these angles are equal":
 * mark two angles with two arcs each and a reader knows they are the same. It has no effect
 * on a right angle, where the square carries the meaning on its own.
 */
export interface AngleMarkSource {
  readonly op: 'angle-mark';
  readonly a: EntityId;
  readonly vertex: EntityId;
  readonly c: EntityId;
  /** 1, 2 or 3 arcs. Ignored when the angle is right. */
  readonly ticks: number;
  /**
   * How large the arc is drawn, as a multiple of the ordinary size.
   *
   * Ordinarily a mark wants no say in this: one size everywhere is what makes a page of angle
   * marks read as one notation. A **projected** figure is the case that breaks it — in a cone
   * or a prism the arms of an angle arrive foreshortened, the wedge closes up, and at the
   * standard size there is no room between the arms for the number that names it. Opening the
   * arc is the only thing that puts the label back inside its own angle.
   *
   * Absent means one, so every existing drawing and every file already saved is unchanged.
   */
  readonly size?: number;
  /** Writes the measured angle beside the mark. */
  readonly showValue: boolean;
  /**
   * Where the user dragged the measure to, as a displacement from where it would sit alone.
   *
   * A displacement rather than a position, so the number keeps its relationship to the mark:
   * move the figure, or drag the angle until it opens differently, and a number the user placed
   * *just there* is still just there. An absolute position would have to be re-dragged after
   * every edit, which for a diagram being built is most of the time.
   *
   * The default placement is good notation and wrong often enough to matter — angles crowded
   * into a corner of a solid, two marks whose numbers land on each other, a number sitting on
   * an edge that happens to pass through it. None of those can be fixed by a better rule,
   * because the thing being avoided is the rest of the drawing.
   *
   * Absent means "wherever the rule puts it", which is what every existing drawing has.
   */
  readonly valueOffset?: Vec2;
  /**
   * Whether the angle is right, when the drawing cannot be asked.
   *
   * ## Why measuring the drawing is not always enough
   *
   * Because an oblique projection does not preserve angles. Only those lying in a plane
   * parallel to the picture plane survive it — which for a cabinet drawing means the front
   * face and nothing else. `SA ⊥ AD` at the foot of a pyramid is a right angle in space and
   * arrives on the page as 60°, so a mark decided by measuring the page refuses to square it;
   * and the converse is worse, since two arms at some other angle in space can project to a
   * clean 90° and earn a square the solid does not deserve.
   *
   * Absent means "measure the drawing", which is right for plane geometry and is what every
   * figure and every saved file did before this existed. Present is a caller that knows the
   * object — the script language, which keeps the space position of every corner it builds.
   */
  readonly right?: boolean;
}

/** A live measurement. Re-evaluates with its targets, which is what makes it a node. */
export interface MeasurementSource {
  readonly op: 'measurement';
  readonly measure: MeasureKind;
  readonly targets: readonly EntityId[];
  readonly offset: Vec2;
}

/* ------------------------------------------------------------------------- union */

export type EntitySource =
  | FreePoint
  | LocusPoint
  | Midpoint
  | RatioPoint
  | SolidFigure
  | SolidVertex
  | SpaceSource
  | PlaneFreeSource
  | PlanePointSource
  | PlaneSource
  | PlaneParallelSource
  | PlaneTurnSource
  | DihedralMarkSource
  | PlaneMeetSource
  | PlaneFootSource
  | SolidRimPoint
  | SolidFoot
  | SegmentMidpoint
  | Intersection
  | ProjectedPoint
  | ReflectedPoint
  | RotatedPoint
  | TranslatedPoint
  | ScaledPoint
  | TriangleCenter
  | CircleCenter
  | SegmentSource
  | RaySource
  | LineSource
  | VectorSource
  | ParallelLine
  | PerpendicularLine
  | PerpendicularBisector
  | AngleBisector
  | TangentLine
  | CircleThrough
  | CircleRadius
  | CircleThreePoints
  | ArcThreePoints
  | ArcCenter
  | EllipseSource
  | EllipseFoci
  | PolygonSource
  | PolygonBoolean
  | RegularPolygonSource
  | FreePolyline
  | FreeEllipse
  | SolidSource
  | ImageSource
  | FunctionGraph
  | AxesSource
  | VariationTableSource
  | GraphTangent
  | GraphMarks
  | PolyhedronSource
  | SolidCutSource
  | TextSource
  | AngleMarkSource
  | MeasurementSource;

export type ConstructionOp = EntitySource['op'];

/* --------------------------------------------------------------------- accessors */

const EMPTY_PARENTS: readonly EntityId[] = Object.freeze([]);

/**
 * The entities this source depends on — the incoming edges of the DAG.
 *
 * Reading parents out of the source rather than storing them separately removes a whole
 * class of bug: there is no second copy of the graph that can drift out of step with the
 * definitions.
 *
 * The exhaustive switch is load-bearing. Adding a variant to `EntitySource` without
 * handling it here is a compile error, so a new construction can never silently join the
 * scene with no dependencies and quietly stop updating.
 */
export function parentsOf(source: EntitySource): readonly EntityId[] {
  switch (source.op) {
    case 'plane':
      return [source.solid, source.a, source.b, source.c];
    case 'plane-parallel':
      return [source.solid, source.through, source.to];
    case 'space':
      return [source.center];
    case 'plane-free':
      return [source.space];
    case 'plane-point':
      return [source.plane];
    case 'plane-turn':
      return [source.solid, source.from, source.a, source.b];
    case 'dihedral-mark':
      return [source.solid, source.a, source.b];
    case 'plane-meet':
      return [source.solid, source.a, source.b];
    case 'plane-foot':
      return [source.solid, source.plane, source.from];

    // A graph joins this run for the same reason the rest are here: it is defined by its own
    // data — a formula and a range — so nothing in the document moves it.
    case 'free-point':
    case 'free-polyline':
    case 'free-ellipse':
    case 'image':
    case 'function-graph':
    case 'axes':
      return EMPTY_PARENTS;

    // A solid hangs off the point it is centred on, and moves with it.
    case 'solid':
      return [source.center];

    // The graph is what the table is *of*: change the formula and the table follows, delete
    // the curve and the table goes with it.
    case 'variation-table':
      return [source.graph];

    // The curve is what the tangent touches: retype the formula and the tangent follows it,
    // delete the curve and there is nothing left for it to be tangent to.
    case 'graph-tangent':
    case 'graph-marks':
      return [source.graph];

    // Every corner: the body follows them, so it moves when any of them does.
    case 'polyhedron':
      return source.corners;

    case 'solid-cut':
      return [source.solid, source.plane, source.keeps];

    case 'locus-point':
      return [source.host];

    case 'solid-figure':
      return [source.center];

    case 'solid-vertex':
    case 'solid-rim-point':
      return [source.solid];

    case 'solid-foot':
      return [source.solid, source.from, source.a, source.b];

    case 'midpoint':
    case 'ratio-point':
    case 'line':
    case 'segment':
    case 'perpendicular-bisector':
      return [source.a, source.b];

    case 'segment-midpoint':
      return [source.segment];

    case 'intersection':
      return [source.a, source.b];

    case 'projected-point':
      return [source.point, source.onto];

    case 'reflected-point':
      return [source.point, source.mirror];

    case 'rotated-point':
    case 'scaled-point':
      return [source.point, source.center];

    case 'translated-point':
      return [source.point, source.vector];

    case 'circumcenter':
    case 'incenter':
    case 'centroid':
    case 'orthocenter':
    case 'circle-3-points':
    case 'arc-3-points':
      return [source.a, source.b, source.c];

    case 'circle-center':
      return [source.circle];

    case 'ray':
      return [source.origin, source.through];

    case 'vector':
      return [source.from, source.to];

    case 'parallel-line':
    case 'perpendicular-line':
      return [source.reference, source.through];

    case 'angle-bisector':
      return [source.a, source.vertex, source.c];

    case 'tangent-line':
      return [source.conic, source.from];

    case 'circle-through':
      return [source.center, source.through];

    case 'circle-radius':
    case 'ellipse':
      return [source.center];

    case 'arc-center':
      return [source.center, source.start, source.end];

    case 'ellipse-foci':
      return [source.f1, source.f2, source.through];

    case 'polygon':
      return source.vertices;

    case 'polygon-boolean':
      return [source.a, source.b];

    case 'regular-polygon':
      return [source.center, source.vertex];

    case 'text':
      return source.anchor === null ? EMPTY_PARENTS : [source.anchor];

    case 'angle-mark':
      return [source.a, source.vertex, source.c];

    case 'measurement':
      return source.targets;
  }
}

/**
 * True when the user can drag this entity directly.
 *
 * Every operation is listed explicitly rather than falling through a `default`. A
 * `default: return false` would mean a newly added construction silently becomes
 * undraggable, and the bug would surface as a tool that simply does nothing. Spelling
 * out both halves forces the decision to be made when the operation is added.
 */
export function isDraggable(source: EntitySource): boolean {
  switch (source.op) {
    /*
     * A graph is moved by editing its formula, not by dragging it. Dragging would have to mean
     * "add a constant", which is a thing the user should say in the formula where it stays
     * visible — a curve silently shifted by three is a curve that no longer matches its label.
     */
    /*
     * Axes are dragged nowhere either. Their origin *is* the origin — a pair of axes moved
     * off it would leave every graph reading against a frame that is no longer where it says.
     */
    case 'function-graph':
    case 'axes':
      return false;

    /*
     * A plane is dragged by dragging what defines it. Moving the wash on its own would detach
     * the section from the corners it was cut through, which is the one thing a computed answer
     * must never allow — and the same reason a midpoint cannot be dragged off its segment.
     */
    case 'plane':
    case 'plane-parallel':
    case 'plane-turn':
    case 'plane-free':
    case 'plane-meet':
    case 'plane-foot':
      return false;

    /*
     * A point on a plane **is** dragged, and this is where that is declared. It slides in the
     * plane's own axes rather than across the page — `SelectTool` rewrites `(u, v)` — which is
     * the same arrangement a `locus-point` has: draggable, and never off its host.
     */
    case 'plane-point':
      return true;

    // A viewpoint is turned from its panel, not shoved about; its anchor is what moves the figure.
    case 'space':
      return false;

    // Moved by moving the angle it marks, which is the only position it can have.
    case 'angle-mark':
    case 'dihedral-mark':
      return false;

    /*
     * A solid is moved by dragging the point it hangs from, not by grabbing the wireframe.
     * Dragging the drawing itself would have to decide whether the gesture means "move it"
     * or "turn it", and the rotate tool already owns the second question.
     */
    case 'solid':
      return false;

    /*
     * A table is a caption, so it goes where there is room on the page. Its *contents* are
     * fixed by the graph — dragging moves the block and never changes a number in it.
     */
    /* Carries its own coordinates, or a parameter the user moves along a host. */
    case 'free-point':
    case 'locus-point':
    case 'free-polyline':
    case 'free-ellipse':
    case 'image':
    case 'text':
    case 'measurement':
    case 'variation-table':
      return true;

    /*
     * A solid's corner is fully determined by the solid, and refusing to drag it is the point.
     * One corner of a cube moved on its own is not a cube, and an editor that allowed it would
     * be one where the figure stops meaning what it says without saying so. Move or resize the
     * solid instead, and every corner follows.
     */
    case 'solid-figure':
    case 'solid-vertex':
    case 'solid-rim-point':
    case 'solid-foot':
    case 'midpoint':
    case 'ratio-point':
    case 'segment-midpoint':
    case 'intersection':
    case 'projected-point':
    case 'reflected-point':
    case 'rotated-point':
    case 'translated-point':
    case 'scaled-point':
    case 'circumcenter':
    case 'incenter':
    case 'centroid':
    case 'orthocenter':
    case 'circle-center':
    case 'segment':
    case 'ray':
    case 'line':
    case 'vector':
    case 'parallel-line':
    case 'perpendicular-line':
    case 'perpendicular-bisector':
    case 'angle-bisector':
    case 'tangent-line':
    case 'graph-tangent':
    case 'graph-marks':
    case 'polyhedron':
    case 'solid-cut':
    case 'circle-through':
    case 'circle-radius':
    case 'circle-3-points':
    case 'arc-3-points':
    case 'arc-center':
    case 'ellipse':
    case 'ellipse-foci':
    case 'polygon':
    case 'polygon-boolean':
    case 'regular-polygon':
      return false;
  }
}

/**
 * True when this source is a construction whose value is fully determined by its
 * parents. Dragging such an entity is meaningless — the tool moves its parents instead.
 */
export function isDerived(source: EntitySource): boolean {
  return parentsOf(source).length > 0 && !isDraggable(source);
}
